import { Component, ElementRef, OnInit, computed, effect, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterModule } from '@angular/router'

import { Store, createSelector } from '@ngrx/store'

import { FlxButtonModule } from '@flowx/angular-ui-toolkit'
import { commandCenterKey } from '@flowx/designer/app.utils'
import { ExecutionPlanComponent } from '@flowx/designer/modules/ai/components/execution-plan/execution-plan.component'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { CommandCenterChatComponent } from '@flowx/designer/modules/ai/modules/command-center-chat/command-center-chat.component'
import { ContextHeaderComponent } from '@flowx/designer/modules/ai/modules/command-center-documents/components/context-header/context-header.component'
import { DocumentsListComponent } from '@flowx/designer/modules/ai/modules/command-center-documents/components/documents-list/documents-list.component'
import { AiFabComponent } from '@flowx/designer/modules/ai/modules/window/components/fab/fab.component'
import { KeyboardService } from '@flowx/designer/shared/services/keyboard.service'
import {
  selectMergedRouteData,
  selectQueryParam,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import { LayoutOptions } from '@flowx/designer/shared/types/layout.types'

import { AiInitApi } from './modules/ai/common/api/ai-init.api'
import { ConversationActions } from './modules/ai/conversation/conversation.actions'
import { AiActions, AiPermissionActions } from './modules/ai/store/ai.actions'
import { AiSelectors } from './modules/ai/store/ai.selectors'
import { MonacoLoaderService } from './modules/monaco-code-editor/monaco-loader.service'
import { ApplicationPermissionsStore } from './modules/projects/store/application-permissions.store'
import { AppEffectsService } from './shared/effector/app/app.effects'
import { NavigationErrorHandlerService } from './shared/services/navigation-error-handler.service'
import { UrlParametersStoreService } from './shared/services/url-parameters-store.service'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterModule,
    FlxButtonModule,
    ContextHeaderComponent,
    ExecutionPlanComponent,
    DocumentsListComponent,
    CommandCenterChatComponent,
    AiFabComponent,
  ],
})
export class AppComponent implements OnInit {
  #appEffectsService = inject(AppEffectsService)
  #monacoLoaderService = inject(MonacoLoaderService)
  #urlParametersStoreService = inject(UrlParametersStoreService)
  #store = inject(Store)
  #aiInitApi = inject(AiInitApi)

  #keyboardService = inject(KeyboardService)
  #applicationPermissionsStore = inject(ApplicationPermissionsStore)
  #navigationErrorHandlerService = inject(NavigationErrorHandlerService)

  el = inject(ElementRef)

  hidingCommandCenter = signal(false)
  showCommandCenter = signal(false)
  showAiFab = this.#store.selectSignal(AiSelectors.isAiFabVisible)
  commandCenterActive = this.#store.selectSignal(ConversationSelectors.commandCenterActivated)
  hasExecutionPlan = this.#store.selectSignal(ConversationSelectors.currentExecutionPlanExists)
  showDocuments = this.#store.selectSignal(ConversationSelectors.showContextDocuments)
  hasActiveConversation = this.#store.selectSignal(ConversationSelectors.activeConversationExists)

  runInstanceActive = this.#store.selectSignal(
    createSelector(selectMergedRouteData, (data) => !!data?.[LayoutOptions.IS_RUNNING_INSTANCE])
  )

  runInstancePanelOpen = this.#store.selectSignal(
    createSelector(selectQueryParam('instanceDataOpened'), (value) => value === 'true')
  )

  effectiveShowAiFab = computed(() => {
    if (!this.showAiFab() || this.commandCenterActive()) {
      return false
    }
    if (this.runInstanceActive()) {
      return this.runInstancePanelOpen()
    }
    return true
  })

  constructor() {
    effect(() => {
      const aiAccessRights = this.#applicationPermissionsStore.aiAccessRights()
      this.#store.dispatch(
        AiPermissionActions.loadAiPermissionsSuccess({ permissions: aiAccessRights })
      )
    })

    effect(() => {
      const commandCenterActive = this.commandCenterActive()

      if (commandCenterActive) {
        localStorage.setItem(commandCenterKey, 'true')
        document.body.classList.add('command-center-active')
        this.showCommandCenter.set(true)
      } else {
        localStorage.removeItem(commandCenterKey)
        document.body.classList.remove('command-center-active')
        this.resetCommandCenter()
      }
    })

    this.#urlParametersStoreService.init()

    this.#keyboardService.metaK
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.#toggleCommandCenter())

    this.#monacoLoaderService.load()
    this.#navigationErrorHandlerService.init()
  }

  ngOnInit(): void {
    // Initialize file upload config for AI conversations
    this.#aiInitApi.init().subscribe((fileUploadConfig) => {
      this.#store.dispatch(ConversationActions.loadFileUploadConfigSuccess({ fileUploadConfig }))
    })
  }

  resetCommandCenter(): void {
    // Re-entrancy guard: the effect on commandCenterActive re-invokes this
    // after the transitionend handler flips showCommandCenter to false.
    // Without this, that second call would re-arm the hiding animation
    // against an already-hidden element and leave hidingCommandCenter stuck.
    if (this.hidingCommandCenter() || !this.showCommandCenter()) {
      return
    }

    const target = this.el.nativeElement.querySelector('.command-center-header')
    if (target) {
      // Pin --pip-row-1-height to the header's measured height so the
      // pip's translateY at the scaled endpoint lands the visual on
      // viewport center (instead of row-2 center).
      const headerHeight = (target as HTMLElement).getBoundingClientRect().height
      ;(this.el.nativeElement as HTMLElement).style.setProperty(
        '--pip-row-1-height',
        `${headerHeight}px`
      )

      const handler = (event: TransitionEvent): void => {
        /**
         * We need to check both the event target and property name because:
         * 1. The header element can have transitions on properties we don't care about
         * 2. Child elements can trigger transitions that bubble up
         * 3. We only care about the hiding animation transitions (transform/opacity) of the header
         * Without these checks, any CSS property change would incorrectly trigger the exit logic
         */
        if (
          event.target === target &&
          (event.propertyName === 'transform' || event.propertyName === 'opacity')
        ) {
          target.removeEventListener('transitionend', handler)
          this.#store.dispatch(AiActions.exitCommandCenter())
          this.hidingCommandCenter.set(false)
          this.showCommandCenter.set(false)
        }
      }
      target.addEventListener('transitionend', handler)
      this.hidingCommandCenter.set(true)
    } else {
      this.#store.dispatch(AiActions.exitCommandCenter())
      this.hidingCommandCenter.set(false)
      this.showCommandCenter.set(false)
    }
  }

  newSession(): void {
    this.#store.dispatch(ConversationActions.newSessionRequested())
  }

  #enterCommandCenter(): void {
    // Cmd/Ctrl+K is a no-op when no AI agent is available for the user
    if (this.commandCenterActive() || !this.showAiFab()) {
      return
    }
    this.#store.dispatch(AiActions.enterCommandCenter())
  }

  #toggleCommandCenter(): void {
    if (this.commandCenterActive()) {
      this.resetCommandCenter()
    } else {
      this.#enterCommandCenter()
    }
  }
}
